import React, { useState } from 'react';
import {
  Modal,
  Stack,
  Text,
  Title,
  Button,
  Group,
  Alert,
  useMantineColorScheme,
  Box,
  Divider,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCalendarTime,
  IconCheck,
  IconX,
  IconInfoCircle,
} from '@tabler/icons-react';
import { UserSubscriptionDTO } from '../../services/userSubscriptionService';

interface Props {
  opened: boolean;
  onClose: () => void;
  subscription: UserSubscriptionDTO | null;
  onConfirm: () => Promise<void>;
  loading: boolean;
}

const CancelSubscriptionModal: React.FC<Props> = ({
  opened,
  onClose,
  subscription,
  onConfirm,
  loading,
}) => {
  const { colorScheme } = useMantineColorScheme();
  const [step, setStep] = useState<'confirmation' | 'processing'>('confirmation');

  const handleConfirm = async () => {
    setStep('processing');
    try {
      await onConfirm();
      onClose(); // Se cierra automáticamente tras el éxito
    } catch (error) {
      setStep('confirmation'); // Vuelve a la confirmación si hay error
    }
  };

  const getEndDate = () => {
    if (!subscription?.endDate) return 'No disponible';
    
    try {
      const date = new Date(subscription.endDate);
      return date.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return 'No disponible';
    }
  };

  const getRemainingDays = () => {
    if (!subscription?.endDate) return 0;
    
    try {
      const endDate = new Date(subscription.endDate);
      const today = new Date();
      const diffTime = endDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays);
    } catch {
      return 0;
    }
  };

  const remainingDays = getRemainingDays();
  const endDateFormatted = getEndDate();
  const hasRemainingAccess = remainingDays > 0;

  const cardStyle = {
    backgroundColor: colorScheme === 'dark' 
      ? 'rgba(30, 41, 59, 0.7)' 
      : 'rgba(247, 243, 238, 0.95)',
    border: `1px solid ${colorScheme === 'dark' 
      ? 'rgba(255, 255, 255, 0.1)' 
      : 'rgba(221, 216, 209, 0.8)'}`,
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="¿Cancelar suscripción?"
      centered
      size="lg"
      radius="lg"
      styles={{
        content: { 
          ...cardStyle, 
          padding: '24px' 
        },
        header: { 
          backgroundColor: 'transparent',
          borderBottom: 'none',
          paddingBottom: '12px',
        },
        title: {
          color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
          fontSize: '1.5rem',
          fontWeight: 600,
        }
      }}
    >
      <Stack gap="md">
        {step === 'confirmation' ? (
          <>
            <Text 
              size="sm" 
              c="dimmed"
            >
              Estás a punto de cancelar tu plan {subscription?.plan?.name || 'actual'}
            </Text>

            {hasRemainingAccess && (
              <Alert
                styles={{
                  root: {
                    backgroundColor: colorScheme === 'dark' 
                      ? 'rgba(34, 197, 94, 0.15)' 
                      : 'rgba(34, 197, 94, 0.1)',
                    border: `1px solid ${colorScheme === 'dark' 
                      ? 'rgba(34, 197, 94, 0.3)' 
                      : 'rgba(34, 197, 94, 0.25)'}`,
                  },
                  message: { 
                    color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26'
                  }
                }}
              >
                <Stack gap="4">
                  <Text size="sm" fw={600}>
                    Mantendrás acceso completo hasta:
                  </Text>
                  <Text size="lg" fw={700}>
                    {endDateFormatted}
                  </Text>
                  <Text size="xs" c="dimmed">
                    ({remainingDays} día{remainingDays !== 1 ? 's' : ''} restante{remainingDays !== 1 ? 's' : ''})
                  </Text>
                </Stack>
              </Alert>
            )}

            <Box>
              <Text 
                size="sm" 
                fw={600} 
                mb="sm"
              >
                Qué pasará después:
              </Text>
              <Stack gap="sm">
                <Group gap="xs" wrap="nowrap" align="flex-start">
                  <IconX size={18} color="#ee6b6e" style={{ flexShrink: 0, marginTop: 2 }} />
                  <Text size="sm" c="dimmed">
                    Se cancelará la renovación automática
                  </Text>
                </Group>
                {hasRemainingAccess && (
                  <Group gap="xs" wrap="nowrap" align="flex-start">
                    <IconCheck size={18} color="#10b981" style={{ flexShrink: 0, marginTop: 2 }} />
                    <Text size="sm" c="dimmed">
                      Conservas todas las funciones premium hasta {endDateFormatted}
                    </Text>
                  </Group>
                )}
                <Group gap="xs" wrap="nowrap" align="flex-start">
                  <IconCheck size={18} color="#10b981" style={{ flexShrink: 0, marginTop: 2 }} />
                  <Text size="sm" c="dimmed">
                    {hasRemainingAccess 
                      ? 'Después cambiarás automáticamente al plan gratuito'
                      : 'Cambiarás inmediatamente al plan gratuito'
                    }
                  </Text>
                </Group>
                <Group gap="xs" wrap="nowrap" align="flex-start">
                  <IconCheck size={18} color="#10b981" style={{ flexShrink: 0, marginTop: 2 }} />
                  <Text size="sm" c="dimmed">
                    Podrás reactivar tu suscripción cuando quieras
                  </Text>
                </Group>
              </Stack>
            </Box>

            <Group justify="flex-end" mt="lg" gap="sm">
              <Button
                variant="subtle"
                onClick={onClose}
                disabled={loading}
                color="gray"
              >
                Mantener suscripción
              </Button>
              <Button
                onClick={handleConfirm}
                loading={loading}
                style={{
                  backgroundColor: '#ee6b6e',
                  color: 'white'
                }}
                rightSection={<IconCheck size={16} />}
              >
                Sí, cancelar plan
              </Button>
            </Group>
          </>
        ) : (
          /* Step de procesamiento */
          <Box ta="center" py="xl">
            <Text 
              size="lg" 
              fw={600} 
              mb="md"
              style={{ 
                color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b' 
              }}
            >
              Procesando cancelación...
            </Text>
            <Text 
              size="sm" 
              style={{ 
                color: colorScheme === 'dark' ? '#94a3b8' : '#64748b' 
              }}
            >
              Por favor espera un momento
            </Text>
          </Box>
        )}
      </Stack>
    </Modal>
  );
};

export default CancelSubscriptionModal;